<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'
import { tagsConfig as rocnovelConfig } from '../configs/rocnovel'
import { tagsConfig as ocereadConfig } from '../configs/oceread'
import { tagsConfig as ficmoonConfig } from '../configs/ficmoon'
import { tagsConfig as novelarcConfig } from '../configs/novelarc'
import { tagsConfig as storyellaConfig } from '../configs/storyella'
import { tagsConfig as aurenovelConfig } from '../configs/aurenovel'
import { tagsConfig as florastoryConfig } from '../configs/florastory'
import { tagsConfig as silktaleConfig } from '../configs/silktale'
import { tagsConfig as pearlaConfig } from '../configs/pearla'
import { tagsConfig as fictoraConfig } from '../configs/fictora'
import { tagsConfig as marinovelConfig } from '../configs/marinovel'
import { brand, resources } from '@/config/brands'
import { loadBrandConfig } from '@/utils/loadBrandConfig'

const props = defineProps({
  tagsList: {
    type: Array as () => Array<{ id: string; isHot: boolean; tagName: string }>,
    required: true,
    default: () => [],
  },
  activeTab: {
    type: Number,
    required: true,
    default: 0,
  },
})

const emit = defineEmits(['update:active-tab', 'toggle-all-tags'])

// 🔥 加载当前品牌配置（构建时 tree-shaking）
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  ficmoon: ficmoonConfig,
  novelarc: novelarcConfig,
  storyella: storyellaConfig,
  aurenovel: aurenovelConfig,
  florastory: florastoryConfig,
  silktale: silktaleConfig,
  pearla: pearlaConfig,
  fictora: fictoraConfig,
  marinovel: marinovelConfig,
  novelmello: rocnovelConfig,
})

// 定义主题变量供 CSS v-bind 使用
const popupTagBgColor = computed(() => config.theme.popupTagBgColor)
const popupTagColor = computed(() => config.theme.popupTagColor)

const showAllTags = ref(false)
const scrollContainer = ref<HTMLElement | null>(null)

function disableScroll() {
  const tagsContainer = document.querySelector('#tagsContainer') as HTMLElement
  tagsContainer.style.position = 'fixed'
  tagsContainer.style.width = '100%'
}

function enableScroll() {
  const tagsContainer = document.querySelector('#tagsContainer') as HTMLElement
  tagsContainer.style.position = ''
  tagsContainer.style.top = ''
  tagsContainer.style.width = ''
}
// 出现下拉框时, 避免页面滚动
watch(
  () => showAllTags.value,
  (val) => {
    if (val)
      disableScroll()

    else
      enableScroll()
  },
)

function toggleAllTags(e?: Event) {
  e?.stopPropagation()
  showAllTags.value = !showAllTags.value
  emit('toggle-all-tags', showAllTags.value)
}

async function handleTagClick(index: number, e?: Event) {
  if (props.activeTab === index)
    return
  if (showAllTags.value)
    showAllTags.value = false
  emit('update:active-tab', index)
  await nextTick(scrollToActiveTag)
}
watch(
  () => props.activeTab,
  () => {
    nextTick(scrollToActiveTag)
  },
  { immediate: true },
)
function scrollToActiveTag() {
  if (!scrollContainer.value) {
    console.error('滚动容器未绑定')
    return
  }

  const buttons = scrollContainer.value.querySelectorAll('button')

  if (buttons.length > props.activeTab) {
    const activeButton = buttons[props.activeTab]
    activeButton.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }
  else {
    console.error('按钮数量不足或索引越界')
  }
}
</script>

<template>
  <div class="sticky top-40px pl-20px pr-36px py-3 z-30">
    <div class="relative">
      <div ref="scrollContainer" class="flex overflow-x-auto hide-scrollbar">
        <div class="flex flex-nowrap">
          <button
            v-for="(tab, index) in tagsList" :key="tab.id || index"
            class="flex-shrink-0 px-3 py-1 text-15px select-none" :class="{
              'text-n-primary font-bold': activeTab === index,
              'text-n-text-3': activeTab !== index,
            }" @click="handleTagClick(index, $event)"
          >
            <!-- talewave: 将火图标设置为内联元素，允许与文字在多行时组合换行 -->
            <template v-if="brand === 'talewave'">
              <span class="inline-block align-middle max-w-full">
                <img v-if="tab.isHot" :src="resources?.tagsIcons?.hotIconRed" class="inline-block align-middle w-3 h-3" alt="火花图标">
                <span class="ml-1 break-words whitespace-normal inline">{{ tab.tagName }}</span>
              </span>
            </template>
            <template v-else>
              <span class="inline-flex items-center">
                <img v-if="tab.isHot" :src="resources?.tagsIcons?.hotIconRed" class="w-3 h-3 flex-shrink-0" alt="火花图标">
                <span class="whitespace-nowrap ml-1">{{ tab.tagName }}</span>
              </span>
            </template>
            <div v-if="activeTab === index" class="mx-auto mt-1 h-3px w-6 rounded-full bg-n-primary" />
          </button>
        </div>
      </div>

      <div class="absolute -right-16px top-0 bottom-0 flex items-center z-10">
        <button class="pointer-events-auto select-none" @click="toggleAllTags($event)">
          <img :src="resources?.tagsIcons?.slicesTags" class="w-4 h-4">
        </button>
      </div>

      <transition name="panel">
        <div v-if="showAllTags" class="fixed inset-0 z-40" @click.self="toggleAllTags">
          <div class="absolute left-0 right-0 top-95px bottom-0 bg-black bg-opacity-50 pointer-events-none " />
          <div class="absolute left-0 right-0 top-94px bg-n-bg-global max-h-[50vh] overflow-y-auto" @click.stop>
            <div class="flex flex-wrap px-5">
              <button
                v-for="(tag, index) in tagsList" :key="tag.id || index"
                class="popup-tag mx-4px my-2.25 px-3 h-28px text-10px rounded-full whitespace-nowrap flex items-center justify-center select-none"
                :class="{
                  'popup-tag--active bg-n-primary text-white font-medium': activeTab === index,
                  'popup-tag--inactive': activeTab !== index,
                }" @click="handleTagClick(index, $event)"
              >
                <!-- 弹出面板：talewave 使用 inline 换行，其他品牌保留原本 nowrap 文本 -->
                <template v-if="brand === 'talewave'">
                  <img
                    v-if="tag.isHot"
                    :src="activeTab === index ? resources?.tagsIcons?.hotIcon : resources?.tagsIcons?.hotIconRed"
                    class="inline-block align-middle w-11px h-11px mr-1" alt="火苗图标"
                  >
                  <span class="align-middle break-words whitespace-normal inline">{{ tag.tagName }}</span>
                </template>
                <template v-else>
                  <img
                    v-if="tag.isHot"
                    :src="activeTab === index ? resources?.tagsIcons?.hotIcon : resources?.tagsIcons?.hotIconRed"
                    class="w-11px h-11px mr-1" alt="火苗图标"
                  >
                  {{ tag.tagName }}
                </template>
              </button>
            </div>
            <div class="sticky bottom-0 left-0 right-0 bg-n-bg-global py-3 flex justify-center">
              <button class="flex items-center justify-center w-3 h-3 select-none" @click="toggleAllTags($event)">
                <img :src="resources?.tagsIcons?.slicesFold" class="w-3 h-3" alt="收起图标">
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.panel-leave-active {
  transition: all 0.3s ease;
}

.panel-enter-from,
.panel-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

button {
  cursor: pointer;
  user-select: none;
}

/* 弹窗标签样式 */
.popup-tag--inactive {
  background-color: v-bind(popupTagBgColor);
  color: v-bind(popupTagColor);
}
</style>
